import React, { useState, useEffect } from 'react';
import { formatPhone } from '../utils/phoneUtils';
import StatusChip from './ui/StatusChip';
import Skeleton from './ui/Skeleton';
import EmptyState from './ui/EmptyState';
import Button from './ui/Button';

const STEP_ORDER = ['pending', 'paid', 'processing', 'ready', 'delivered'];

const STEP_LABELS = {
  pending: 'Pedido recebido',
  paid: 'Pagamento confirmado',
  processing: 'Em produção',
  ready: 'Pronto para entrega',
  delivered: 'Entregue',
};

const getPaymentMethodText = (method) => {
  const methods = {
    cash: 'Dinheiro',
    pix: 'PIX',
    card: 'Cartão',
    credit_card: 'Cartão de Crédito',
    boleto: 'Boleto',
    other: 'Outro',
  };
  return methods[method?.toLowerCase()] || method;
};

const itemName = (item) => item.product?.name || item.product_name || item.product_type || 'Produto';

const OrderTracking = ({ token, onNewOrder }) => {
  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchOrderStatus = async () => {
      if (!token) {
        setError('Informe o link de acompanhamento que recebemos no seu WhatsApp.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/orders/by-token/${token}/`,
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (!response.ok) throw new Error('not-found');
        const data = await response.json();
        setOrderData(Array.isArray(data) ? data[0] : data);
      } catch {
        setError('Não foi possível carregar os dados do pedido. Verifique o link recebido.');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) fetchOrderStatus();
  }, [token]);

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard indisponível: o token já está visível na tela */
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-100 px-4 py-10">
        <div className="mx-auto max-w-3xl space-y-4">
          <Skeleton className="h-28 w-full rounded-card" />
          <Skeleton className="h-10 w-2/3 rounded-card" />
          <Skeleton className="h-48 w-full rounded-card" />
        </div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-cream-100 px-4 py-10">
        <div className="mx-auto max-w-md pt-16">
          <EmptyState
            title="Pedido não encontrado"
            description={error}
            action={<Button onClick={onNewOrder}>Fazer um novo pedido</Button>}
          />
        </div>
      </div>
    );
  }

  const status = orderData.status?.toLowerCase();
  const isCancelled = status === 'cancelled';
  const currentStep = STEP_ORDER.indexOf(status);

  return (
    <div className="min-h-screen bg-cream-100 px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Cabeçalho */}
        <header className="rounded-card bg-cocoa-800 px-6 py-7 text-cream-50 shadow-lift sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cream-50/60">
                Arttibus · Acompanhamento
              </p>
              <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight">
                Pedido #{orderData.id}
              </h1>
            </div>
            <button
              onClick={onNewOrder}
              className="rounded-full border border-cream-50/30 px-4 py-2 text-sm font-semibold text-cream-50 transition-colors hover:bg-cream-50/10"
            >
              Novo pedido
            </button>
          </div>
          <div className="mt-5">
            <StatusChip status={orderData.status} />
          </div>
        </header>

        {/* Timeline */}
        {isCancelled ? (
          <section className="rounded-card border border-clay-200 bg-clay-50 px-6 py-5">
            <p className="text-sm font-semibold text-clay-700">
              Este pedido foi cancelado. Se foi um engano, fale com a gente pelo WhatsApp.
            </p>
          </section>
        ) : (
          <section className="rounded-card border border-cocoa-100 bg-white px-6 py-6 shadow-card">
            <h2 className="mb-5 font-display text-lg font-semibold text-ink">
              Como está o seu pedido
            </h2>
            <ol className="space-y-0">
              {STEP_ORDER.map((step, index) => {
                const done = currentStep > index;
                const active = currentStep === index;

                return (
                  <li key={step} className="relative flex gap-4 pb-6 last:pb-0">
                    {index < STEP_ORDER.length - 1 && (
                      <span
                        aria-hidden="true"
                        className={`absolute left-[13px] top-7 h-[calc(100%-16px)] w-0.5 ${
                          done ? 'bg-sage-300' : 'bg-cocoa-100'
                        }`}
                      />
                    )}
                    <span
                      aria-hidden="true"
                      className={`z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        done
                          ? 'bg-sage-600 text-white'
                          : active
                          ? 'bg-cocoa-800 text-cream-50 ring-4 ring-cocoa-100'
                          : 'border border-cocoa-200 bg-white text-ink-muted'
                      }`}
                    >
                      {done ? (
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </span>
                    <div className="pt-0.5">
                      <p
                        className={`text-sm ${
                          active
                            ? 'font-bold text-ink'
                            : done
                            ? 'font-medium text-ink-soft'
                            : 'text-ink-muted'
                        }`}
                      >
                        {STEP_LABELS[step]}
                      </p>
                      {active && (
                        <p className="mt-0.5 text-xs text-caramel-600">Etapa atual</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* Resumo + observações */}
        <div className="grid gap-6 sm:grid-cols-2">
          <section className="rounded-card border border-cocoa-100 bg-white px-6 py-5 shadow-card">
            <h2 className="mb-4 font-display text-lg font-semibold text-ink">Resumo</h2>
            <dl className="space-y-2.5 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Data</dt>
                <dd className="text-right font-medium text-ink">
                  {orderData.created_at ? new Date(orderData.created_at).toLocaleString('pt-BR') : '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Telefone</dt>
                <dd className="font-medium text-ink">{formatPhone(orderData.phone_number) || '—'}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Pagamento</dt>
                <dd className="font-medium text-ink">{getPaymentMethodText(orderData.payment_method) || '—'}</dd>
              </div>
              {Number(orderData.discount) > 0 && (
                <div className="flex justify-between gap-4">
                  <dt className="text-sage-600">Desconto</dt>
                  <dd className="font-medium text-sage-600">
                    - R$ {Number(orderData.discount).toFixed(2)}
                  </dd>
                </div>
              )}
              {Number(orderData.shipping_fee) > 0 && (
                <div className="flex justify-between gap-4">
                  <dt className="text-ink-muted">Frete</dt>
                  <dd className="font-medium text-ink">{Number(orderData.shipping_fee).toFixed(2)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4 border-t border-cocoa-100 pt-2.5">
                <dt className="font-semibold text-ink">Total</dt>
                <dd className="font-display text-lg font-semibold text-cocoa-800">
                  R$ {orderData.total_price ? parseFloat(orderData.total_price).toFixed(2) : '0,00'}
                </dd>
              </div>
            </dl>
          </section>

          {(orderData.observation || orderData.notes) && (
            <section className="rounded-card border border-cocoa-100 bg-white px-6 py-5 shadow-card">
              <h2 className="mb-3 font-display text-lg font-semibold text-ink">Observações</h2>
              <p className="text-sm leading-relaxed text-ink-soft">
                {orderData.observation || orderData.notes}
              </p>
            </section>
          )}
        </div>

        {/* Itens */}
        <section className="rounded-card border border-cocoa-100 bg-white px-6 py-5 shadow-card">
          <h2 className="mb-4 font-display text-lg font-semibold text-ink">Itens do pedido</h2>
          {orderData.order_items && orderData.order_items.length > 0 ? (
            <ul className="divide-y divide-cocoa-100">
              {orderData.order_items.map((item, index) => (
                <li key={item.id ?? index} className="flex gap-4 py-3.5 first:pt-0 last:pb-0">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-cream-100">
                    {item.image ? (
                      <img src={item.image} alt={`Arte de ${itemName(item)}`} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-cocoa-300">
                        <svg className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.16-5.16a2.25 2.25 0 013.18 0l5.16 5.16m-1.5-9l.01 12a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25V6.75A2.25 2.25 0 014.5 4.5h12A2.25 2.25 0 018.75 6.75V15" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">{itemName(item)}</p>
                    <p className="mt-0.5 text-xs text-ink-muted">
                      {item.quantity} × R$ {Number(item.unit_price ?? 0).toFixed(2)}
                    </p>
                  </div>
                  <p className="text-sm font-bold text-cocoa-800">
                    R$ {(Number(item.unit_price ?? 0) * item.quantity).toFixed(2)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-muted">Nenhum item neste pedido.</p>
          )}
        </section>

        {/* Token */}
        <section className="rounded-card border border-caramel-200 bg-caramel-50 px-6 py-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-ink">Código de acompanhamento</h2>
              <p className="mt-1 break-all font-mono text-xs text-ink-soft">{token}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={copyToken}>
              {copied ? 'Copiado!' : 'Copiar código'}
            </Button>
          </div>
        </section>

        <p className="pb-6 text-center text-xs text-ink-muted">
          Dúvidas? Chame a Arttibus no Instagram{' '}
          <a href="https://www.instagram.com/arttibus/" target="_blank" rel="noreferrer" className="font-semibold text-cocoa-700 underline-offset-2 hover:underline">
            @arttibus
          </a>
        </p>
      </div>
    </div>
  );
};

export default OrderTracking;
